/**
 * # Time Typeahead
 * Listen to an input element and format it as the user types.
 *
 * @example
 * new TimeTypeahead(el);
 *
 * @module components/time-typeahead.js
 */

import Typeahead from './typeahead';
import triggerEvent from '../helpers/dom/trigger-event';

const noop = function() {};

class TimeTypeahead extends Typeahead {

  /**
   * Typeahead constructor
   * @param {Element} el
   * @param {Object} params
   */
  constructor(el, params = {}) {
    params.elRequired = true;

    if (!super(el, params)) {
      return;
    }
  }


  /**
   * Run the formatting.
   * @param {Number} cursorIndex
   */
  run(cursorIndex, params = {}) {
    if (this.isRunning) return this;

    this.isRunning = true;
    let oldVal = this.inputEl.value;
    let val = '';
    let placeholder = '';
    let len = this.format.length;
    let skipCount = 0;
    let valDone = false;

    for (let i = 0; i < len; i++) {

      // Add numbers, letters or placeholders
      if (this.format[i] === '\\d') {

        if (this.characters[i - skipCount]) {
          val += this.characters[i - skipCount];
        } else {
          valDone = true;
        }

        placeholder += valDone && this.placeholder[i] ? this.placeholder[i] : '&nbsp;';
      } else if (this.format[i] === '\\D') {
        if (this.characters[i - skipCount]) {
          val += this.characters[i - skipCount];
        } else {
          valDone = true;
        }

        placeholder += valDone && this.placeholder[i] ? this.placeholder[i] : '&nbsp;';
      } else {

        if (!valDone) {
          val += this.format[i];
        }

        placeholder += this.format[i];

        skipCount++;
      }
    }

    if (this.isFocused) {
      cursorIndex = cursorIndex === undefined ? this._getCaretEnd() : cursorIndex;
    }

    // If there are no characters, set the cursorIndex to be the last placeholder entry.
    if (this.isFocused && !this.characters.length) {
      cursorIndex = val.length;
    }

    // No characters and we shouldn't use just placeholder values
    if (!this.characters.length && params.notOnlyPlaceholders) {
      val = '';
    }

    this.inputEl.value = val;
    this.placeholderEl.innerHTML = placeholder;

    this._updateWidth();

    if (this.isFocused) {
      this._setCaretPositionTranslated(cursorIndex);
    }

    if (val !== oldVal) {
      triggerEvent(this.inputEl, 'input');
    }

    this.isRunning = false;

    if (val !== oldVal) {
      (this.onChange || noop)(val, oldVal, this);
    }

    if (!this._atEnd && this.isFocused && this.characters.length === this.maxLength && this._caretIsAtEnd()) {
      this._atEnd = true;
      (this.onEnd || noop)(this);
    } else {
      this._atEnd = false;
    }

    return this;
  }


  /**
   * Set the value of the typeahead. Maintain the position of the caret.
   * @param {String} value
   */
  setValue(value) {
    this.settingValue = true;
    this.pause();

    if (typeof value === 'string') {

      // Ensure that periods are not stripped from a.m./p.m.
      if (this.placeholder !== 'a.m.') {
        this.characters = (value.replace(/\W/g, '') + '').split('');
      } else {
        this.characters = value.split('');
      }

    } else if (typeof value === 'number') {

      // Perform regex replace if a float is set
      if (value % 1 !== 0) {
        this.characters = (value.toString().replace(/\W/g, '') + '').split('');
      } else {
        this.characters = (value + '').split('');
      }
    }

    this.run();

    if (this.isFocused) this._setCaretPosition(this._getCaretStart());

    this.resume();
    this.settingValue = false;

    return this;
  }


  /**
   * Create the default input element.
   * @return {Element}
   */
  _createDefaultInputElement() {
    let el = document.createElement('input');
    el.className = 'spark-input__field';
    el.setAttribute('data-typeahead', '');

    if (this.placeholder === 'a.m.') {
      el.setAttribute('type', 'text');
    } else {
      el.setAttribute('type', 'tel');
    }

    this.el.appendChild(el);

    return el;
  }


  /**
   * Get the maximum number of characters allowed.
   * @param {Array} format
   * @return {Number}
   */
  _getCharactersAllowedCount(format) {
    const len = format.length;
    let allowed = 0;

    for (let i = 0; i < len; i++) {
      if (format[i] === '\\d' || format[i] === '\\D') {
        allowed++;
      }
    }

    return allowed;
  }


  /**
   * Get the position of the caret translated to the corresponding index in the
   * characters array. This means ignoring format characters.
   * @param {Number} pos
   * @return {Number}
   */
  _getCaretPositionTranslated(pos) {
    let i = 0;
    let skipCount = 0;

    for (; i < pos; i++) {
      if (this.format[i] !== '\\d' && this.format[i] !== '\\D') {
        skipCount++;
      }
    }

    return pos - skipCount;
  }


  /**
   * Set the position of the caret translated to the corresponding index in the
   * characters array. This means ignoring format characters.
   * @param {Number} pos
   */
  _setCaretPositionTranslated(pos) {
    let i = 0;
    let skipCount = 0;

    for (; i < pos + skipCount; i++) {
      if (this.format[i] !== undefined && this.format[i] !== '\\d' && this.format[i] !== '\\D') {
        skipCount++;
      }
    }

    this._setCaretPosition(pos + skipCount);
  }


  /**
   * Remove characters from the period field
   * @param  {String} action Action can be Backspace or Delete
   * @param  {Number} caretStart
   * @param  {Number} inputLength
   */
  _removePeriodCharacters(action, caretStart, inputLength) {
    let zeroBasedAdjustment = action === 'BACKSPACE' ? 1 : 0;
    let caretPos = action === 'BACKSPACE' ? -1 : 0;

    for (let c = 0; c < (inputLength - caretStart + zeroBasedAdjustment); c++) {

      if (caretStart === 1 && action === 'BACKSPACE') {
        this.inputEl.setSelectionRange((caretStart - 1), inputLength);
        this.removeCharactersInRange();
        break;
      } else {
        this.removeCharacterAtCaret(caretPos);
      }

      caretPos = caretPos === -1 ? 0 : -1;
    }
  }


  /**
   * Listen for delete and arrows.
   * @param  {Object} e
   */
  _onKeydown(e) {
    let code = e.keyCode || e.which;

    if (code === this.pasteCode && (e.metaKey || e.ctrlKey)) {
      return;
    }

    if (code === this.actionCodes.BACKSPACE) {
      if (this.placeholder === 'a.m.') {

        let caretStart = this._getCaretStart();
        let inputLength = this.inputEl.value.length;

        this._removePeriodCharacters('BACKSPACE', caretStart, inputLength);
      } else {
        this.removeCharacterAtCaret(-1);
      }

      this._onBackspace();
      e.preventDefault();
    } else if (code === this.actionCodes.DELETE) {
      if (this.placeholder === 'a.m.') {

        let caretStart = this._getCaretStart();
        let inputLength = this.inputEl.value.length;

        this._removePeriodCharacters('DELETE', caretStart, inputLength);
      } else {
        this.removeCharacterAtCaret(0);
      }

      e.preventDefault();
    } else if (code === this.actionCodes.LEFT) {
      if (!this._getCaretStart()) {
        (this.onBackspace || noop)();
      }
    } else if (code === this.actionCodes.RIGHT) {
      if (this._getCaretStart() === this.characters.length) {
        (this.onEnd || noop)();
      }
    } else {
      if (this.ignoreCodes.indexOf(code) === -1) {
        e.preventDefault();

        // Account for Numpad keys
        if (code >= 96 && code <= 105) {
          code -= 48;
        }

        if (this.placeholder === 'a.m.') {

          const enteredCharacter = String.fromCharCode(code).toLowerCase();

          // If the contents of the field have been selected, set cursor position to beginning of selection
          let selectedText = '';
          selectedText = this.inputEl.value.substring(this.inputEl.selectionStart, this.inputEl.selectionEnd);

          if (selectedText !== '') {
            this._setCaretPosition(this._getCaretStart());
          }

          if (this.inputEl.value) {
            let inputVal = this.inputEl.value.toLowerCase();

            if (inputVal === 'a' || inputVal === 'p' || inputVal === 'a.m' || inputVal === 'p.m') {

              // Allow m only if we have a or p. Append . before the m
              if (code === 77 && (inputVal === 'a' || inputVal === 'p')) {
                this.addCharacterAtCaret('.');
                this.addCharacterAtCaret(enteredCharacter);
              }

              // Allow . only
              if (code === 190) {
                this.addCharacterAtCaret('.');
              }
            } else if (inputVal === 'a.' || inputVal === 'p.') {

              // Allow m only
              if (code === 77) {
                this.addCharacterAtCaret(enteredCharacter);
              }
            } else if (inputVal === 'a.m.' || inputVal === 'p.m.') {

              // Check position of cursor to determine what's allowed
              let currPos = this._getCaretStart();

              if (currPos === 0) {

                // Allow a or p
                if (enteredCharacter === 'a' || enteredCharacter === 'p') {
                  this.removeCharacterAtCaret(0);
                  this._moveCaret('left');
                  this.addCharacterAtCaret(enteredCharacter);
                }
              }
              if (currPos === 1) {

                // Allow .
                if (code === 190) {
                  this.removeCharacterAtCaret(0);
                  this._moveCaret('left');
                  this.addCharacterAtCaret('.');
                }
              }
              if (currPos === 2) {

                // Allow m only
                if (code === 77) {
                  this.removeCharacterAtCaret(0);
                  this._moveCaret('left');
                  this.addCharacterAtCaret(enteredCharacter);
                }
              }
              if (currPos === 3) {

                // Allow .
                if (code === 190) {
                  this.removeCharacterAtCaret(0);
                  this.addCharacterAtCaret('.');
                }
              }
            } else {
              return false;
            }
          } else {
            if (enteredCharacter === 'a' || enteredCharacter === 'p') {
              this.addCharacterAtCaret(enteredCharacter);
            }
          }
        } else {
          this.addCharacterAtCaret(String.fromCharCode(code));
        }
      }
    }
  }
}


/**
 * Whitelisted parameters which can be set on construction.
 * @type {Array}
 */
TimeTypeahead.prototype._whitelistedParams = [
  'format',
  'placeholder',
  'matchPlaceholderSize',
  'onChange',
  'onFocus',
  'onBlur',
  'onInput',
  'onBackspace',
  'onEnd'
];


/**
 * Default values for internal properties we will be setting.
 * These are set on each construction so we don't leak properties
 * into the prototype chain.
 * @type {Object}
 */
TimeTypeahead.prototype.defaults = {
  el: null,
  inputEl: null,
  placeholderEl: null,
  placeholder: null,
  characters: null,
  format: null,
  ignoreCodes: [

    // Tab
    9,

    // Shift
    16,

    // Ctrl
    17,

    // Alt
    18,

    // CAPS
    20,

    // Meta
    91,

    // Alt
    93,

    // F1-F12
    112, 113, 114, 115, 116, 117, 118, 119, 120, 121, 122, 123,

    // Arrows
    38, 40
  ],
  actionCodes: {
    BACKSPACE: 8,
    DELETE: 46,
    LEFT: 37,
    RIGHT: 39
  },

  // v
  pasteCode: 86,
  pauseBlurFocus: 0,
  matchPlaceholderSize: null,
  maxLength: null,
  isFocused: false,
  isRunning: false,
  onChange: null,
  onFocus: null,
  onBlur: null,
  onInput: null,
  onBackspace: null,
  onEnd: null,
  _atEnd: false,
  _oldVal: null,
  _onKeydownBound: null,
  _onKeypressBound: null,
  _onFocusBound: null,
  _onBlurBound: null,
  _onInputBound: null,
  _onPlaceholderClickBound: null
};

export default TimeTypeahead;
